import { useEffect, useMemo, useRef, useState } from 'react';
import type { Selection } from 'react-aria-components';
import { pluralizeWord } from '@gonfalon/format/pluralizeWord';
import {
  Button,
  ButtonGroup,
  Checkbox,
  Focusable,
  Group,
  Input,
  ListBox,
  ListBoxItem,
  MenuTrigger,
  Popover,
  Separator,
  Tooltip,
  TooltipTrigger,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { clsx } from 'clsx';

import styles from './MultiSelectFilter.module.css';

export type FilterOption<T = unknown> = {
  id: string;
  label: string;
  data?: T;
  isDisabled?: boolean;
  disabledTooltip?: string;
};

type MultiSelectFilterProps<T = unknown> = {
  label: string;
  options: Array<FilterOption<T>>;
  selectedValues: string[];
  onChange: (values: string[]) => void;
  renderOption?: (option: FilterOption<T>) => React.ReactNode;
  /** Custom render function for the header checkbox label. Receives the label string and returns element */
  renderHeaderLabel?: (label: string) => React.ReactNode;
  /** Custom render function for the button display. Receives display text, selected options, and count */
  renderButtonDisplay?: (params: {
    displayText: string;
    selectedOptions: Array<FilterOption<T>>;
    isAllSelected: boolean;
  }) => React.ReactNode;
  /**
   * If true, applies selection changes immediately without Apply/Cancel buttons.
   * If false (default), shows Apply/Cancel buttons and only applies on Apply click.
   * In both modes, the popover stays open to allow multiple selections.
   */
  applySelectionOnChange?: boolean;
  /**
   * If true (default), shows a "Select all" checkbox at the top of the options list.
   * If false, hides the select-all checkbox and only shows individual options.
   */
  canSelectAll?: boolean;
  /**
   * If true (default), shows the chevron-down icon next to the button text.
   * If false, hides the chevron (useful for action-style buttons like "Subscribe").
   */
  showChevron?: boolean;
  /**
   * Controls how an empty selectedValues array is interpreted:
   * - 'none' (default): Empty means no items are selected (explicit selection behavior)
   * - 'all': Empty means all items are selected (filter behavior - no filtering applied)
   */
  emptySelectionBehavior?: 'all' | 'none';
  /**
   * If true (default), allows applying changes with no selections (empty state).
   * If false, requires at least one selection - Apply button is disabled when none selected.
   */
  isEmptySelectionAllowed?: boolean;
  /**
   * If true, shows a search input at the top of the options list.
   * Parent component handles search logic via onSearchChange callback.
   */
  isSearchEnabled?: boolean;
  /**
   * Current search value (controlled input).
   * If provided, the search input will be controlled by the parent.
   * If undefined, the input will be uncontrolled (not recommended).
   */
  searchValue?: string;
  /**
   * Placeholder text for the search input.
   */
  searchPlaceholder?: string;
  /**
   * Callback when search input value changes.
   * Parent component should handle API calls and pass updated options.
   */
  onSearchChange?: (query: string) => void;
  /**
   * If true, shows a loading spinner in the options list (e.g. during search).
   */
  isSearching?: boolean;
  /**
   * Optional subheader text to display between search/header and the options list.
   * Useful for instructions or context (e.g. "Select up to ten channels to receive Slack notifications...")
   */
  subheaderText?: string;
  /**
   * Optional custom render function for empty state (when no options available and not searching).
   * If not provided, the empty list is shown by default.
   */
  renderEmptyState?: () => React.ReactNode;
  /**
   * Optional render function for content below the options list (e.g. informational messages).
   * Renders after the ListBox/empty state but before any action buttons.
   */
  renderFooter?: () => React.ReactNode;
  /**
   * Callback when the popover opens or closes.
   * Useful for cleanup or state management when modal state changes.
   */
  onOpenChange?: (isOpen: boolean) => void;
  className?: string;
  buttonClassName?: string;
  popoverClassName?: string;
  disabled?: boolean;
};

/**
 * A multi-select filter component with indeterminate checkbox state.
 *
 * ## Features
 * - All options selected by default when no filters applied
 * - Optional "Select all" checkbox with indeterminate state (shows '-' when some selected)
 * - Two selection modes: immediate apply or Apply/Cancel buttons
 * - Popover stays open for multiple selections in both modes
 * - Disabled state when no options selected (in Apply/Cancel mode)
 * - Customizable option rendering
 *
 * ## Selection Modes
 * - `applySelectionOnChange={false}` (default): Shows Apply/Cancel buttons, applies on button click
 * - `applySelectionOnChange={true}`: Applies immediately, no buttons
 *
 * ## Select All Checkbox
 * - `canSelectAll={true}` (default): Shows a "Select all" checkbox at the top
 * - `canSelectAll={false}`: Hides the select-all checkbox, shows only individual options
 *
 * ## Static Lists
 * This component is designed for static option lists. For dynamic/async data or search functionality,
 * pre-filter your options before passing them to this component.
 *
 * ## Extensibility
 * This component is in the experimental package and can be extended for additional use cases.
 * Contributions and enhancements are welcome!
 */
export function MultiSelectFilter<T = unknown>({
  label,
  options,
  selectedValues = [],
  onChange,
  renderOption,
  renderHeaderLabel,
  renderButtonDisplay,
  applySelectionOnChange = false,
  canSelectAll = true,
  showChevron = true,
  emptySelectionBehavior = 'none',
  isEmptySelectionAllowed = true,
  isSearchEnabled = false,
  searchValue,
  searchPlaceholder = 'Search...',
  onSearchChange,
  isSearching = false,
  subheaderText,
  renderEmptyState,
  renderFooter,
  onOpenChange,
  className,
  buttonClassName,
  popoverClassName,
  disabled = false,
}: MultiSelectFilterProps<T>) {
  const filterOptions = useMemo(() => options.filter((o) => o.id !== ''), [options]);

  // All option IDs, excluding disabled options (which cannot be toggled)
  const allOptionIds = useMemo(() => filterOptions.filter((o) => !o.isDisabled).map((o) => o.id), [filterOptions]);

  const [pendingSelection, setPendingSelection] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const prevIsOpenRef = useRef(isOpen);
  // Preserve the allOptionIds from when the popover opens to avoid hasChanges false positives
  // when options prop changes during search
  const allOptionIdsAtOpenRef = useRef<string[]>([]);

  // Initialize pending selection only when popover transitions to open
  // If no selections and emptySelectionBehavior is 'all', default to all checked
  // Don't react to selectedValues changes while popover is already open
  useEffect(() => {
    const justOpened = isOpen && !prevIsOpenRef.current;

    if (justOpened) {
      // Capture the current allOptionIds at open time
      allOptionIdsAtOpenRef.current = allOptionIds;
      const initialSelection =
        selectedValues.length === 0 && emptySelectionBehavior === 'all' ? allOptionIds : selectedValues;
      setPendingSelection(initialSelection);
    }

    prevIsOpenRef.current = isOpen;
  }, [isOpen, selectedValues, allOptionIds, emptySelectionBehavior]);

  // Convert pending selection to Set for ListBox
  // Only include IDs that exist in current filterOptions (filtered-out items shouldn't be in selectedKeys)
  const visibleOptionIds = useMemo(() => new Set(filterOptions.map((opt) => opt.id)), [filterOptions]);

  const visibleSelections = useMemo(
    () => pendingSelection.filter((id) => visibleOptionIds.has(id)),
    [pendingSelection, visibleOptionIds],
  );

  const selectedKeys = useMemo(() => new Set(visibleSelections), [visibleSelections]);

  // Checkbox state based on VISIBLE selections only (to handle search filtering correctly)
  // Only count non-disabled options for select-all checkbox state
  const visibleEnabledCount = useMemo(() => filterOptions.filter((opt) => !opt.isDisabled).length, [filterOptions]);
  const someSelected = visibleSelections.length > 0 && visibleSelections.length < visibleEnabledCount;
  const allSelected = visibleSelections.length === visibleEnabledCount && visibleEnabledCount > 0;

  // Check if pending selection differs from current selection
  // When selectedValues is empty and emptySelectionBehavior is 'all', it means "all selected"
  // Use allOptionIdsAtOpenRef to ensure stability when options change during search
  const currentSelection = useMemo(() => {
    if (selectedValues.length === 0 && emptySelectionBehavior === 'all') {
      // Use the preserved allOptionIds from open time to avoid false positives during search
      return allOptionIdsAtOpenRef.current.length > 0 ? allOptionIdsAtOpenRef.current : allOptionIds;
    }
    return selectedValues;
  }, [selectedValues, emptySelectionBehavior, allOptionIds]);

  const hasChanges = useMemo(() => {
    if (currentSelection.length !== pendingSelection.length) {
      return true;
    }
    const currentSet = new Set(currentSelection);
    return pendingSelection.some((id) => !currentSet.has(id));
  }, [currentSelection, pendingSelection]);

  // Display text based on applied filters
  const displayText = useMemo(() => {
    if (selectedValues.length === 0) {
      return emptySelectionBehavior === 'all'
        ? `All ${pluralizeWord(label.toLowerCase())}`
        : `0 ${pluralizeWord(label.toLowerCase())}`;
    }
    if (selectedValues.length === 1) {
      const option = filterOptions.find((o) => o.id === selectedValues[0]);
      return option?.label || label;
    }
    return `${selectedValues.length} ${pluralizeWord(label.toLowerCase())}`;
  }, [selectedValues, filterOptions, label, emptySelectionBehavior]);

  // Get selected options for custom rendering
  const selectedOptions = useMemo(() => {
    if (selectedValues.length === 0) {
      return emptySelectionBehavior === 'all' ? filterOptions : [];
    }
    return filterOptions.filter((o) => selectedValues.includes(o.id));
  }, [selectedValues, filterOptions, emptySelectionBehavior]);

  const handleCheckboxChange = () => {
    // Toggle between all VISIBLE *enabled* items selected and none selected
    // Exclude disabled items from select-all toggle
    const visibleEnabledIds = filterOptions.filter((opt) => !opt.isDisabled).map((opt) => opt.id);
    const newVisibleSelection = allSelected || someSelected ? [] : visibleEnabledIds;

    // Preserve selections that are currently filtered out of view
    const filteredOutSelections = pendingSelection.filter((id) => !visibleOptionIds.has(id));

    // Combine: new selections for visible items + preserved filtered-out items
    const newSelection = [...newVisibleSelection, ...filteredOutSelections];

    // In immediate-apply mode, prevent empty selection to avoid desyncing
    // pendingSelection from the parent's selectedValues (the parent would
    // reject the empty onChange, but pendingSelection would already be []).
    if (applySelectionOnChange && !isEmptySelectionAllowed && newSelection.length === 0) {
      return;
    }

    setPendingSelection(newSelection);

    // Apply immediately if applySelectionOnChange is enabled
    if (applySelectionOnChange) {
      // If emptySelectionBehavior is 'all', convert "all selected" to empty array
      // Only convert if ALL options (not just visible) are selected
      // Use allOptionIdsAtOpenRef to ensure stability when options change during search
      const allIdsCount =
        allOptionIdsAtOpenRef.current.length > 0 ? allOptionIdsAtOpenRef.current.length : allOptionIds.length;
      const valueToSend = emptySelectionBehavior === 'all' && newSelection.length === allIdsCount ? [] : newSelection;
      onChange(valueToSend);
    }
  };

  const handleSelectionChange = (keys: Selection) => {
    const selectedArray = Array.from(keys).map(String);

    // Preserve selections that are currently filtered out of view
    // Find selections that are filtered out (not in current options)
    const filteredOutSelections = pendingSelection.filter((id) => !visibleOptionIds.has(id));

    // Combine: new selections from visible items + preserved filtered-out items
    const newPendingSelection = [...selectedArray, ...filteredOutSelections];

    if (applySelectionOnChange && !isEmptySelectionAllowed && newPendingSelection.length === 0) {
      return;
    }

    setPendingSelection(newPendingSelection);

    // Apply immediately if applySelectionOnChange is enabled
    if (applySelectionOnChange) {
      // If emptySelectionBehavior is 'all', convert "all selected" to empty array
      // Only convert if ALL options (not just visible) are selected
      // Use allOptionIdsAtOpenRef to ensure stability when options change during search
      const allIdsCount =
        allOptionIdsAtOpenRef.current.length > 0 ? allOptionIdsAtOpenRef.current.length : allOptionIds.length;
      const valueToSend =
        emptySelectionBehavior === 'all' && newPendingSelection.length === allIdsCount ? [] : newPendingSelection;
      onChange(valueToSend);
    }
  };

  const handleApply = () => {
    // If emptySelectionBehavior is 'all', convert "all selected" to empty array
    // Only convert if ALL options (not just visible) are selected
    // Use allOptionIdsAtOpenRef to ensure stability when options change during search
    const allIdsCount =
      allOptionIdsAtOpenRef.current.length > 0 ? allOptionIdsAtOpenRef.current.length : allOptionIds.length;
    const valueToSend =
      emptySelectionBehavior === 'all' && pendingSelection.length === allIdsCount ? [] : pendingSelection;

    onChange(valueToSend);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  // Use pendingSelection.length (all selections) not noneSelected (only visible selections)
  // to ensure consistency with what handleApply will send
  const isApplyButtonDisabled = (!isEmptySelectionAllowed && pendingSelection.length === 0) || !hasChanges;

  return (
    <MenuTrigger
      isOpen={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        onOpenChange?.(open);
      }}
    >
      <Group className={clsx(styles.filterGroup, className)}>
        <Button isDisabled={disabled} className={clsx(styles.filterButton, buttonClassName)}>
          {renderButtonDisplay ? (
            renderButtonDisplay({
              displayText,
              selectedOptions,
              isAllSelected: selectedValues.length === 0 && emptySelectionBehavior === 'all',
            })
          ) : (
            <span>{displayText}</span>
          )}
          {showChevron && <Icon name="chevron-down" size="small" />}
        </Button>
      </Group>
      <Popover className={popoverClassName || styles.filterPopover} placement="bottom start" offset={4} crossOffset={0}>
        <div className={clsx(styles.filterPopoverContent)}>
          {isSearchEnabled && (
            <div className={styles.searchContainer}>
              <div className={styles.searchInputWrapper}>
                <Icon name="search" size="small" className={styles.searchIcon} />
                <Input
                  type="search"
                  autoFocus
                  value={searchValue}
                  placeholder={searchPlaceholder}
                  onChange={(e) => onSearchChange?.(e.target.value)}
                  aria-label="Search options"
                  className={styles.searchInput}
                />
              </div>
            </div>
          )}
          {canSelectAll && (
            <>
              <div className={styles.filterHeader}>
                <Checkbox
                  isIndeterminate={someSelected}
                  isSelected={allSelected}
                  onChange={handleCheckboxChange}
                  aria-label={allSelected ? 'Deselect all' : 'Select all'}
                >
                  {renderHeaderLabel
                    ? renderHeaderLabel(`All ${pluralizeWord(label.toLowerCase())}`)
                    : `All ${pluralizeWord(label.toLowerCase())}`}
                </Checkbox>
              </div>
              <Separator />
            </>
          )}
          {subheaderText && (
            <>
              <div className={styles.subheaderText}>{subheaderText}</div>
              {(isSearching || filterOptions.length > 0 || renderEmptyState || renderFooter) && <Separator />}
            </>
          )}
          {isSearching ? (
            <div className={styles.loadingContainer} role="status" aria-label="Loading...">
              <div className={styles.loadingSkeletonItem} />
              <div className={styles.loadingSkeletonItem} />
              <div className={styles.loadingSkeletonItem} />
              <div className={styles.loadingSkeletonItem} />
              <div className={styles.loadingSkeletonItem} />
            </div>
          ) : filterOptions.length === 0 && renderEmptyState ? (
            <div className={styles.emptyStateContainer}>{renderEmptyState()}</div>
          ) : (
            <ListBox
              selectionMode="multiple"
              selectedKeys={selectedKeys}
              onSelectionChange={handleSelectionChange}
              aria-label={`${label} filter options`}
            >
              {filterOptions.map((option) => {
                const content = renderOption ? renderOption(option) : option.label;

                const listBoxItem = (
                  <ListBoxItem key={option.id} id={option.id} textValue={option.label} isDisabled={option.isDisabled}>
                    {option.isDisabled && option.disabledTooltip ? (
                      <Focusable>
                        <span role="button" tabIndex={-1} style={{ cursor: 'not-allowed' }}>
                          {content}
                        </span>
                      </Focusable>
                    ) : (
                      content
                    )}
                  </ListBoxItem>
                );

                // Add tooltip wrapper for disabled items
                if (option.isDisabled && option.disabledTooltip) {
                  return (
                    <TooltipTrigger key={option.id}>
                      {listBoxItem}
                      <Tooltip placement="right">{option.disabledTooltip}</Tooltip>
                    </TooltipTrigger>
                  );
                }

                return listBoxItem;
              })}
            </ListBox>
          )}
          {renderFooter && renderFooter()}
          {!applySelectionOnChange && (
            <>
              <Separator />
              <div className={styles.filterActions}>
                <ButtonGroup>
                  <Button size="small" variant="default" onPress={handleCancel}>
                    Cancel
                  </Button>
                  <TooltipTrigger delay={0} isDisabled={!isApplyButtonDisabled}>
                    <Focusable>
                      <Button size="small" variant="primary" onPress={handleApply} isDisabled={isApplyButtonDisabled}>
                        Apply
                      </Button>
                    </Focusable>
                    <Tooltip>
                      {!isEmptySelectionAllowed && pendingSelection.length === 0
                        ? 'Select at least one option'
                        : 'No changes to apply'}
                    </Tooltip>
                  </TooltipTrigger>
                </ButtonGroup>
              </div>
            </>
          )}
        </div>
      </Popover>
    </MenuTrigger>
  );
}
